/**
 * Expansión pura de un `WaveDef` en la lista concreta de apariciones de
 * enemigos: tipo, hp/ataque ya escalados por `hpMul`/`atkMul`, y orden de
 * entrada (intercalado entre grupos, mezclado de forma determinista con
 * `Rng` para que la secuencia varíe entre semillas sin perder el
 * intercalado). Sin Phaser — testeado con Vitest.
 */

import type { WaveDef, WaveEnemyDef } from '../../core/types';
import type { Rng } from '../../core/rng';

export interface EnemyBaseStats {
  hp: number;
  atk: number;
  speed: number; // px/seg a velocidad x1
}

/** Stats base por tipo de enemigo conocido; los desconocidos caen a `DEFAULT_ENEMY_STATS`. */
export const ENEMY_BASE_STATS: Record<string, EnemyBaseStats> = {
  slime: { hp: 20, atk: 4, speed: 34 },
  goblin: { hp: 32, atk: 7, speed: 46 },
  bat: { hp: 14, atk: 5, speed: 64 },
  // 4º tipo común (cripta/volcán en adelante): más resistente que el murciélago,
  // algo más lenta, silueta bien distinta (arácnido).
  spider: { hp: 18, atk: 6, speed: 48 },
};

/** Stats por defecto para cualquier tipo que no esté en `ENEMY_BASE_STATS`. */
export const DEFAULT_ENEMY_STATS: EnemyBaseStats = { hp: 20, atk: 4, speed: 34 };

/** Stats base de jefe: cualquier tipo cuyo id contenga "boss" cae aquí. */
export const DEFAULT_BOSS_STATS: EnemyBaseStats = { hp: 160, atk: 12, speed: 20 };

/** Stats base de un tipo de enemigo dado (por id de `WaveEnemyDef.type`). */
export function baseStatsFor(type: string): EnemyBaseStats {
  if (type.includes('boss')) return DEFAULT_BOSS_STATS;
  return ENEMY_BASE_STATS[type] ?? DEFAULT_ENEMY_STATS;
}

export interface SpawnInstance {
  type: string;
  hp: number;
  atk: number;
  speed: number;
  isBoss: boolean;
  order: number; // posición en la secuencia de aparición (0-based)
}

const SPAWN_STAGGER_MS = 900;

export interface WaveSpawnPlan {
  spawns: SpawnInstance[];
  isBossWave: boolean;
}

/**
 * Expande un `WaveDef` en la lista concreta de enemigos a aparecer, con
 * stats escaladas y orden de entrada intercalado entre grupos (para que no
 * entren todos los enemigos de un tipo antes que los de otro).
 */
export function buildWaveSpawnPlan(def: WaveDef, rng: Rng): WaveSpawnPlan {
  const groups = def.enemies.map((group) => expandGroup(group));
  const interleaved = interleaveGroups(groups, rng);
  const spawns = interleaved.map((spawn, order) => ({ ...spawn, order }));
  return { spawns, isBossWave: def.isBoss ?? false };
}

function expandGroup(group: WaveEnemyDef): SpawnInstance[] {
  const base = baseStatsFor(group.type);
  const isBoss = group.type.includes('boss');
  const list: SpawnInstance[] = [];
  for (let i = 0; i < group.count; i++) {
    list.push({
      type: group.type,
      hp: Math.max(1, Math.round(base.hp * group.hpMul)),
      atk: Math.max(1, Math.round(base.atk * group.atkMul)),
      speed: base.speed,
      isBoss,
      order: 0,
    });
  }
  return list;
}

/** Intercala varios grupos en round-robin, mezclando el orden de los grupos en cada ronda. */
function interleaveGroups(groups: SpawnInstance[][], rng: Rng): SpawnInstance[] {
  const result: SpawnInstance[] = [];
  const maxLen = groups.reduce((max, g) => Math.max(max, g.length), 0);
  for (let i = 0; i < maxLen; i++) {
    const groupOrder = rng.shuffle(groups.map((_, idx) => idx));
    for (const idx of groupOrder) {
      const item = groups[idx]?.[i];
      if (item) result.push(item);
    }
  }
  return result;
}

/** Retardo (ms) de aparición del enemigo n-ésimo, a velocidad x1. */
export function spawnDelayMs(order: number): number {
  return order * SPAWN_STAGGER_MS;
}
